import { useState, useEffect } from 'react';
import { checkAiReadiness } from '../../services/ai/engine';
import { AI_SETTINGS_CHANGED_EVENT } from '../../services/ai/aiSettings';
import { VAULT_GATE_UNLOCKED_EVENT, VAULT_UNLOCKED_UI_EVENT } from '../../store/vaultStore';

/**
 * BYO-key readiness for the AI panel, re-checked while it is open.
 */
export function useAiReadiness(isOpen) {
  // BYO-key readiness: null while checking, else { state, provider, model }
  // from checkAiReadiness. Anything but state:'ready' renders the setup
  // state instead of the chat empty state (the post-proxy first-run UX).
  const [aiReadiness, setAiReadiness] = useState(null);

  // Re-check key/vault readiness whenever the panel opens, and again when
  // the AI settings change (key saved/removed, provider switched) or the
  // vault unlocks by ANY route (the gate modal, the vault panel, an
  // extension's silent unlock — both events together cover all of them).
  //
  // LOCKING is covered too, and through a different mechanism on purpose:
  // `vault:locked` is a TAURI event emitted from Rust (auto-lock after idle,
  // the Lock button, logout), not a DOM event — so it needs `listen()`, not
  // addEventListener. The first version of this effect subscribed only to the
  // DOM unlock events, which made unlock reactive and lock not: a panel left
  // open past the idle auto-lock still looked chat-ready until it was closed
  // and reopened, then failed on the first send.
  useEffect(() => {
    if (!isOpen) return undefined;
    let stale = false;
    let unlistenLocked = null;
    const check = () => {
      checkAiReadiness().then((r) => { if (!stale) setAiReadiness(r); });
    };
    check();
    window.addEventListener(AI_SETTINGS_CHANGED_EVENT, check);
    window.addEventListener(VAULT_GATE_UNLOCKED_EVENT, check);
    window.addEventListener(VAULT_UNLOCKED_UI_EVENT, check);

    import('@tauri-apps/api/event')
      .then(({ listen }) => listen('vault:locked', check))
      .then((un) => {
        if (stale) un();
        else unlistenLocked = un;
      })
      .catch(() => {
        /* browser dev mode: no Tauri, and no vault to lock either. */
      });

    return () => {
      stale = true;
      window.removeEventListener(AI_SETTINGS_CHANGED_EVENT, check);
      window.removeEventListener(VAULT_GATE_UNLOCKED_EVENT, check);
      window.removeEventListener(VAULT_UNLOCKED_UI_EVENT, check);
      if (unlistenLocked) unlistenLocked();
    };
  }, [isOpen]);

  return aiReadiness;
}
